import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faSearch } from "@fortawesome/free-solid-svg-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Report } from "../../reports/vouchers_report/TransactionReport";
import TablePaginationActions from "../../../../base-components/TablePaginationActions";
import dayjs from "dayjs";
import {
  PageHeader,
  StatCard,
  EmptyState,
  TableShell,
  StatusChip,
  initials,
} from "../../../../ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import { motion, useReducedMotion } from "motion/react";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const controlClass =
  "border border-gx-border bg-gx-surface rounded-gx-control py-2.5 px-3 outline-0 w-full text-sm text-gx-ink min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gx-red";

const isDebitTransaction = (type) => {
  const t = type?.toLowerCase();
  return t === "invoice" || t === "debit";
};

const getTransactionTypeLabel = (type) => {
  if (isDebitTransaction(type)) return "Debit";
  if (type?.toLowerCase() === "payment" || type?.toLowerCase() === "credit") return "Credit";
  return type || "—";
};

const matchesTransactionTypeFilter = (transaction, filterType) => {
  if (!filterType) return true;
  const type = transaction.transaction_type?.toLowerCase();
  const filter = filterType.toLowerCase();
  if (filter === "debit") return isDebitTransaction(transaction.transaction_type);
  if (filter === "credit") return type === "payment" || type === "credit";
  return type === filter;
};

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return `${value ?? 0} Rs`;
  return `Rs ${n.toLocaleString("en-US")}`;
}

function formatMaybeDate(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM DD, YYYY") : "—";
}

function memberCode(transaction) {
  return `${transaction?.location_id?.member_id_temp ?? ""}${transaction?.member_id?.member_id ?? ""}`;
}

function TransactionsSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading transactions</span>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={`kpi-${i}`} className="gx-glass rounded-gx px-5 py-5 min-h-[132px]">
            <div className="h-3 w-24 rounded-gx bg-gx-border/70 animate-pulse" />
            <div className="h-10 w-20 rounded-gx bg-gx-border/70 animate-pulse mt-6" />
          </div>
        ))}
      </div>
      <div className="gx-glass rounded-gx min-h-[140px] p-5">
        <div className="h-4 w-40 rounded-gx bg-gx-border/70 animate-pulse" />
        <div className="h-11 w-full rounded-gx bg-gx-border/70 animate-pulse mt-4" />
      </div>
    </div>
  );
}

function Transactions() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [searchParams] = useSearchParams();
  const voucher_id = searchParams.get("voucher_id");
  const subscription_id = searchParams.get("subscription_id");
  const company_id = searchParams.get("company_id");
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [role, setRole] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [transactionType, setSubscriptionType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("amount");
  const [layout, setLayout] = useState("cards");

  useEffect(() => {
    const sessionStr = sessionStorage.getItem("user-info");
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      setRole(session.user.role);
    } else {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const load = async () => {
      if (!role) return;
      setLoading(true);
      if (voucher_id) {
        await getTransactions("get-transactions-by-voucher-id", voucher_id);
      } else if (subscription_id) {
        await getTransactions("get-transactions-by-subscription-id", subscription_id);
      } else if (company_id) {
        await getTransactions("get-transactions-by-company-id", company_id);
      } else {
        setTransactions([]);
      }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, voucher_id, subscription_id, company_id]);

  const getTransactions = async (endpoint, id) => {
    await fetch(BASE_URL + `transaction/${endpoint}/${id}`)
      .then((response) => response.json())
      .then((data) => {
        setTransactions(data.transactions || []);
      })
      .catch((error) => {
        console.log(error);
        setTransactions([]);
      });
  };

  const scopedTransactions = useMemo(() => {
    let filtered = transactions || [];
    const q = (searchQuery || "").trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((transaction) => {
        const invoice = String(transaction.voucher_id?.invoice_no || "").toLowerCase();
        const type = String(transaction.transaction_type || "").toLowerCase();
        const label = getTransactionTypeLabel(transaction.transaction_type).toLowerCase();
        const amount = String(transaction.amount ?? "");
        const name = String(transaction.member_id?.name || "").toLowerCase();
        const code = memberCode(transaction).toLowerCase();
        return (
          invoice.includes(q) ||
          type.includes(q) ||
          label.includes(q) ||
          amount.includes(q) ||
          name.includes(q) ||
          code.includes(q)
        );
      });
    }
    if (fromDate && toDate) {
      const startFilterDate = new Date(fromDate);
      startFilterDate.setHours(0, 0, 0, 0);
      const endFilterDate = new Date(toDate);
      endFilterDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((transaction) => {
        if (!transaction?.transaction_date) return false;
        const transactionDate = new Date(transaction.transaction_date);
        return transactionDate >= startFilterDate && transactionDate <= endFilterDate;
      });
    }
    return filtered;
  }, [transactions, searchQuery, fromDate, toDate]);

  const filtered_transaction = useMemo(() => {
    if (!transactionType) return scopedTransactions;
    return scopedTransactions.filter((transaction) =>
      matchesTransactionTypeFilter(transaction, transactionType)
    );
  }, [scopedTransactions, transactionType]);

  const kpis = useMemo(() => {
    const list = scopedTransactions || [];
    let debit = 0;
    let credit = 0;
    list.forEach((tx) => {
      const amount = Number(tx.amount) || 0;
      if (isDebitTransaction(tx.transaction_type)) debit += amount;
      else credit += amount;
    });
    return {
      total: list.length,
      debit,
      credit,
      net: credit - debit,
    };
  }, [scopedTransactions]);

  const paginatedTransactions =
    !filtered_transaction || filtered_transaction.length === 0
      ? []
      : rowsPerPage === -1
        ? filtered_transaction
        : filtered_transaction.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleNavigate = () => {
    if (subscription_id) navigate("/ledgers");
    else navigate(-1);
  };
  const handleDownload = () => Report(filtered_transaction, viewMode);
  const handleClear = () => {
    setSearchQuery("");
    setFromDate("");
    setToDate("");
    setSubscriptionType("");
    setPage(0);
  };
  const selectType = (key) => {
    setSubscriptionType((prev) => (prev === key ? "" : key));
    setPage(0);
  };

  const filtersActive = Boolean(searchQuery || fromDate || toDate || transactionType);
  const memberName = transactions[0]?.member_id?.name;

  const stagger = (index) =>
    reduceMotion
      ? false
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: {
            duration: 0.45,
            delay: index * 0.06,
            ease: [0.16, 1, 0.3, 1],
          },
        };

  const kpiCards = [
    {
      key: "",
      label: "Entries",
      value: kpis.total.toLocaleString("en-US"),
      icon: <ReceiptLongIcon fontSize="small" />,
      accent: "#022A3A",
      hint: "In this period",
      featured: !transactionType,
    },
    {
      key: "Debit",
      label: "Debits",
      value: formatMoney(kpis.debit),
      icon: <SouthWestIcon fontSize="small" />,
      accent: "#D71920",
      hint: "Charges posted",
      featured: transactionType === "Debit",
    },
    {
      key: "Credit",
      label: "Credits",
      value: formatMoney(kpis.credit),
      icon: <NorthEastIcon fontSize="small" />,
      accent: "#047857",
      hint: "Payments received",
      featured: transactionType === "Credit",
    },
    {
      key: "net",
      label: "Net movement",
      value: formatMoney(kpis.net),
      icon: <AccountBalanceWalletIcon fontSize="small" />,
      accent: kpis.net >= 0 ? "#047857" : "#D71920",
      hint: "Credits minus debits",
      featured: false,
    },
  ];

  const renderCard = (transaction) => {
    const debit = isDebitTransaction(transaction?.transaction_type);
    const label = getTransactionTypeLabel(transaction?.transaction_type);
    return (
      <article
        key={transaction?._id}
        className="group rounded-gx border border-gx-border bg-gx-canvas/40 hover:bg-gx-surface hover:border-gx-navy/25 hover:shadow-gx transition overflow-hidden"
      >
        <div className="h-1 bg-gradient-to-r from-[#D71920] to-[#F7931E]" />
        <div className="p-4 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-gx overflow-hidden border border-gx-border shrink-0 bg-gx-navy/10 flex items-center justify-center text-sm font-semibold text-gx-navy">
              {initials(transaction?.member_id?.name)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-gx-ink truncate">
                {transaction?.member_id?.name || "Unknown member"}
              </p>
              <p className="text-xs text-gx-ink-soft truncate">
                {memberCode(transaction) || "No ID"} · {transaction?.voucher_id?.invoice_no || "No voucher"}
              </p>
              <div className="flex flex-wrap gap-1 mt-2">
                <StatusChip tone={debit ? "danger" : "success"} label={label} />
              </div>
            </div>
            <p className={`text-lg font-bold tabular-nums ${debit ? "text-gx-red" : "text-emerald-700"}`}>
              {debit ? "−" : "+"}
              {formatMoney(transaction?.amount || 0).replace("Rs ", "")}
            </p>
          </div>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div className="min-w-0">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">Date</dt>
              <dd className="text-gx-ink">{formatMaybeDate(transaction?.transaction_date)}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">Balance after</dt>
              <dd className="font-semibold tabular-nums text-gx-ink">
                {formatMoney(transaction?.balance_after || 0)}
              </dd>
            </div>
            {viewMode === "ledger" ? (
              <>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">Debit</dt>
                  <dd className="tabular-nums">{debit ? formatMoney(transaction?.amount) : "Rs 0"}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">Credit</dt>
                  <dd className="tabular-nums">{debit ? "Rs 0" : formatMoney(transaction?.amount)}</dd>
                </div>
              </>
            ) : null}
          </dl>
        </div>
      </article>
    );
  };

  return (
    <div className="relative m-5">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-8 left-1/2 h-56 w-[min(100%,920px)] -translate-x-1/2"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(2, 42, 58, 0.07), transparent 70%)",
        }}
      />

      <PageHeader
        title="Transactions"
        subtitle={
          memberName
            ? `Financial log for ${memberName}`
            : "Every debit and credit on this membership, in one ledger."
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="gx-cta-secondary min-h-[44px] gap-2"
              onClick={handleNavigate}
            >
              <ArrowBackIcon fontSize="small" aria-hidden="true" />
              Back
            </button>
            <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
              View
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                className={`${controlClass} mt-1.5 min-w-[12rem]`}
              >
                <option value="amount">Amount & type</option>
                <option value="ledger">Credit / Debit</option>
              </select>
            </label>
            <button type="button" className="gx-cta min-h-[44px] gap-2 self-end" onClick={handleDownload}>
              <FontAwesomeIcon icon={faDownload} aria-hidden="true" />
              Export
            </button>
          </div>
        }
      />

      {loading ? (
        <TransactionsSkeleton />
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            {kpiCards.map((stat, index) => (
              <motion.div key={stat.label} {...stagger(index)}>
                <StatCard
                  featured={stat.featured}
                  label={stat.label}
                  value={stat.value}
                  icon={stat.icon}
                  accent={stat.accent}
                  hint={stat.featured && stat.key ? "Showing this roster" : stat.hint}
                  onClick={stat.key && stat.key !== "net" ? () => selectType(stat.key) : undefined}
                />
              </motion.div>
            ))}
          </div>

          <section className="gx-glass rounded-gx p-4 md:p-5">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
              <div>
                <h2 className="text-sm font-semibold text-gx-ink">Find a transaction</h2>
                <p className="text-xs text-gx-ink-soft mt-0.5">
                  Showing {filtered_transaction.length.toLocaleString("en-US")} of{" "}
                  {(transactions || []).length.toLocaleString("en-US")} entries
                </p>
              </div>
              <div className="flex items-center gap-1 rounded-gx-control border border-gx-border bg-gx-surface p-1 self-start">
                <button
                  type="button"
                  aria-pressed={layout === "cards"}
                  className={`min-h-[44px] min-w-[44px] rounded-gx-control px-3 text-sm font-semibold cursor-pointer transition ${
                    layout === "cards" ? "bg-gx-navy text-white" : "text-gx-navy hover:bg-gx-canvas"
                  }`}
                  onClick={() => setLayout("cards")}
                >
                  <ViewModuleIcon fontSize="small" aria-hidden="true" />
                  <span className="sr-only">Card layout</span>
                </button>
                <button
                  type="button"
                  aria-pressed={layout === "table"}
                  className={`min-h-[44px] min-w-[44px] rounded-gx-control px-3 text-sm font-semibold cursor-pointer transition hidden md:inline-flex items-center justify-center ${
                    layout === "table" ? "bg-gx-navy text-white" : "text-gx-navy hover:bg-gx-canvas"
                  }`}
                  onClick={() => setLayout("table")}
                >
                  <ViewListIcon fontSize="small" aria-hidden="true" />
                  <span className="sr-only">Table layout</span>
                </button>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                setPage(0);
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft xl:col-span-2">
                  Search
                  <div className="relative mt-1.5">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gx-ink-soft"
                      aria-hidden="true"
                    />
                    <input
                      type="search"
                      placeholder="Name, voucher no, type, or amount"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(0);
                      }}
                      className={`${controlClass} pl-10`}
                    />
                  </div>
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                  From
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setPage(0);
                    }}
                    className={`${controlClass} mt-1.5`}
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                  To
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setPage(0);
                    }}
                    disabled={!fromDate}
                    min={fromDate}
                    className={`${controlClass} mt-1.5 ${
                      !fromDate ? "bg-gx-canvas cursor-not-allowed text-gx-ink-soft" : ""
                    }`}
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                  Type
                  <select
                    value={transactionType}
                    onChange={(e) => {
                      setSubscriptionType(e.target.value);
                      setPage(0);
                    }}
                    className={`${controlClass} mt-1.5`}
                  >
                    <option value="">All types</option>
                    <option value="Credit">Credit</option>
                    <option value="Debit">Debit</option>
                  </select>
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="submit" className="gx-cta min-h-[44px]">
                  Apply filters
                </button>
                <button type="button" className="gx-cta-secondary min-h-[44px]" onClick={handleClear}>
                  Clear
                </button>
              </div>
            </form>
          </section>

          {filtered_transaction.length === 0 ? (
            <div className="bg-gx-surface rounded-gx border border-gx-border shadow-gx">
              <EmptyState
                title={filtersActive ? "No matching transactions" : "No transactions yet"}
                description={
                  filtersActive
                    ? "Try a different voucher number, date range, or clear the current filters."
                    : "Debits and credits will appear here once this membership is billed."
                }
                action={
                  filtersActive ? (
                    <button type="button" className="gx-cta-secondary min-h-[44px]" onClick={handleClear}>
                      Clear filters
                    </button>
                  ) : null
                }
              />
            </div>
          ) : (
            <div className="space-y-4">
              {layout === "table" ? (
                <>
                  <TableShell className="hidden md:block">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Voucher</TableCell>
                          <TableCell>Member</TableCell>
                          {viewMode === "ledger" ? (
                            <>
                              <TableCell>Debit</TableCell>
                              <TableCell>Credit</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>Amount</TableCell>
                              <TableCell>Type</TableCell>
                            </>
                          )}
                          <TableCell>Balance after</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedTransactions.map((transaction) => {
                          const debit = isDebitTransaction(transaction?.transaction_type);
                          return (
                            <TableRow hover key={transaction?._id}>
                              <TableCell>{formatMaybeDate(transaction?.transaction_date)}</TableCell>
                              <TableCell>{transaction?.voucher_id?.invoice_no || "—"}</TableCell>
                              <TableCell>
                                <p className="font-semibold text-gx-ink">
                                  {transaction?.member_id?.name || "Unknown"}
                                </p>
                                <p className="text-xs text-gx-ink-soft">{memberCode(transaction) || "—"}</p>
                              </TableCell>
                              {viewMode === "ledger" ? (
                                <>
                                  <TableCell className="tabular-nums">
                                    {debit ? formatMoney(transaction?.amount) : "Rs 0"}
                                  </TableCell>
                                  <TableCell className="tabular-nums">
                                    {debit ? "Rs 0" : formatMoney(transaction?.amount)}
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell>
                                    <span
                                      className={`font-semibold tabular-nums ${
                                        debit ? "text-gx-red" : "text-emerald-700"
                                      }`}
                                    >
                                      {formatMoney(transaction?.amount)}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <StatusChip
                                      tone={debit ? "danger" : "success"}
                                      label={getTransactionTypeLabel(transaction?.transaction_type)}
                                    />
                                  </TableCell>
                                </>
                              )}
                              <TableCell className="font-semibold tabular-nums">
                                {formatMoney(transaction?.balance_after || 0)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableShell>
                  <div className="md:hidden grid grid-cols-1 gap-4">
                    {paginatedTransactions.map(renderCard)}
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {paginatedTransactions.map(renderCard)}
                </div>
              )}
              <div className="bg-gx-surface rounded-gx border border-gx-border shadow-gx overflow-x-auto">
                <Table>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                        colSpan={1}
                        count={filtered_transaction.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        slotProps={{
                          select: {
                            inputProps: { "aria-label": "rows per page" },
                            native: true,
                          },
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActions}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Transactions;
